import { useRef } from 'react';

/**
 * A closed list for `in_any_of` / `not_in_any_of` on a picklist field that opts in with
 * `valueInput: 'closed-list'`: every option is shown as a tickable row, so a rep sees the
 * choices instead of guessing what to type. There is no free typing.
 *
 * It writes exactly what the free-entry chips input writes: an array of stored option
 * values, appended in the order ticked. A value already in the filter that is not in the
 * list (the choices narrowed after it was ticked) shows as a ticked "Unavailable" row, so
 * the rep can see it and untick it; it is never silently dropped. Native checkboxes give Space-to-toggle;
 * the arrow keys move between rows.
 */
export interface ClosedListValuePickerProps {
  value: string[];
  options: Array<{ value: string; label: string }>;
  onChange: (value: string[]) => void;
  /** The group's accessible name, e.g. 'Priority is any of', so two conditions on one field announce differently. */
  ariaLabel?: string;
}

export function ClosedListValuePicker({ value, options, onChange, ariaLabel }: ClosedListValuePickerProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const toggle = (optionValue: string) =>
    onChange(value.includes(optionValue) ? value.filter(v => v !== optionValue) : [...value, optionValue]);

  const moveFocus = (from: HTMLElement, step: 1 | -1) => {
    const boxes = Array.from(groupRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? []);
    boxes[boxes.findIndex(b => b === from) + step]?.focus();
  };

  const unavailable = value.filter(v => !options.some(o => o.value === v));
  const rows = [...options, ...unavailable.map(v => ({ value: v, label: `Unavailable: ${v}` }))];

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label={ariaLabel}
      className="flex flex-col gap-0.5 px-1 py-1 border border-secondary rounded-md bg-white dark:bg-nightshade-800"
    >
      {rows.map(o => (
        <label
          key={o.value}
          className="flex items-center gap-1.5 px-1 py-0.5 rounded text-[11.5px] text-content-primary dark:text-nightshade-100 cursor-pointer hover:bg-bg-secondary"
        >
          <input
            type="checkbox"
            checked={value.includes(o.value)}
            onChange={() => toggle(o.value)}
            onKeyDown={e => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                moveFocus(e.currentTarget, e.key === 'ArrowDown' ? 1 : -1);
              }
            }}
            className="h-3.5 w-3.5 shrink-0 rounded border-secondary text-content-action focus:ring-1 focus:ring-content-action"
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}
